import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, FileText, Trash2, Download, Loader2, ShieldCheck, AlertTriangle, AlertCircle, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Layout } from "../components/Layout";
import CredentialUploadDialog from "../components/CredentialUploadDialog";

function StatusBadge({ status }) {
  const map = {
    "Active":        { cls: "bg-[#E6F0EB] text-[#34745C] border-[#34745C]/20", icon: ShieldCheck },
    "Expiring Soon": { cls: "bg-[#FDF3E1] text-[#C48231] border-[#C48231]/20", icon: AlertTriangle },
    "Expired":       { cls: "bg-[#FCEAE8] text-[#B54D40] border-[#B54D40]/20", icon: AlertCircle },
  };
  const m = map[status] || map["Active"];
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 ${m.cls} border px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap`}>
      <Icon className="w-3 h-3" strokeWidth={2} /> {status}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function CredentialsPage() {
  const [creds, setCreds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [credsR, empR] = await Promise.all([
        api.get("/credentials", { params: statusFilter ? { status: statusFilter } : {} }),
        api.get("/caregivers"),
      ]);
      setCreds(credsR.data.items || []);
      setEmployees(empR.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    if (!search.trim()) return creds;
    const q = search.toLowerCase();
    return creds.filter((c) =>
      (c.caregiver_name || "").toLowerCase().includes(q)
      || (c.credential_type_label || "").toLowerCase().includes(q)
      || (c.issuing_authority || "").toLowerCase().includes(q)
    );
  }, [creds, search]);

  const counts = useMemo(() => {
    const c = { total: creds.length, active: 0, expiring: 0, expired: 0 };
    creds.forEach((x) => {
      if (x.status === "Active") c.active++;
      else if (x.status === "Expiring Soon") c.expiring++;
      else if (x.status === "Expired") c.expired++;
    });
    return c;
  }, [creds]);

  async function downloadFile(c) {
    try {
      const res = await api.get(`/credentials/${c.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = c.file_name || `credential-${c.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  async function remove(c) {
    if (!confirm(`Delete this ${c.credential_type_label} for ${c.caregiver_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/credentials/${c.id}`);
      toast.success("Credential deleted.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  return (
    <Layout>
      <div data-testid="credentials-page" className="space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Workforce compliance</p>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-[#1C211F]">Credentials</h1>
            <p className="mt-2 text-sm text-[#57605A] max-w-2xl">
              Track every certification, license, and required document across your workforce. Status updates automatically as expirations approach.
            </p>
          </div>
          <button
            data-testid="credentials-upload-btn"
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors rounded-md px-5 py-2.5 text-sm font-medium"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Upload credential
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { id: "total",    label: "Total credentials",  value: counts.total,    cls: "border-[#E8E6E1] text-[#1C211F]" },
            { id: "active",   label: "Active",             value: counts.active,   cls: "border-[#34745C]/30 text-[#34745C]" },
            { id: "expiring", label: "Expiring ≤30 days",  value: counts.expiring, cls: "border-[#C48231]/30 text-[#C48231]" },
            { id: "expired",  label: "Expired",            value: counts.expired,  cls: "border-[#B54D40]/30 text-[#B54D40]" },
          ].map((k) => (
            <div key={k.id} data-testid={`creds-kpi-${k.id}`} className={`bg-white border ${k.cls} rounded-xl p-5`}>
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#57605A]">{k.label}</p>
              <p className="mt-2 font-heading text-3xl">{k.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#57605A]" strokeWidth={1.5} />
            <input
              data-testid="creds-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by employee, credential, or authority…"
              className="w-full pl-10 pr-3 py-2.5 bg-white border border-[#E8E6E1] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]"
            />
          </div>
          <div className="inline-flex items-center gap-2 bg-white border border-[#E8E6E1] rounded-md px-3 py-2">
            <Filter className="w-4 h-4 text-[#57605A]" strokeWidth={1.5} />
            <select data-testid="creds-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-transparent text-sm focus:outline-none">
              <option value="">All statuses</option>
              <option value="Active">Active</option>
              <option value="Expiring Soon">Expiring soon</option>
              <option value="Expired">Expired</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E8E6E1] rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-5 h-5 text-[#2E5C50] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="creds-empty-state" className="flex flex-col items-center justify-center py-16 text-center px-4">
              <ShieldCheck className="w-10 h-10 text-[#E8E6E1] mb-3" strokeWidth={1.5} />
              <p className="text-sm font-semibold text-[#1C211F]">No credentials yet</p>
              <p className="mt-1 text-sm text-[#57605A]">Click "Upload credential" to add the first one.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F9F7F4] text-[#57605A] text-xs uppercase tracking-[0.12em]">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Employee</th>
                  <th className="text-left py-3 px-4 font-semibold">Credential</th>
                  <th className="text-left py-3 px-4 font-semibold">Authority</th>
                  <th className="text-left py-3 px-4 font-semibold">Expires</th>
                  <th className="text-left py-3 px-4 font-semibold">Status</th>
                  <th className="text-right py-3 px-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`creds-row-${c.id}`} className="border-t border-[#E8E6E1] hover:bg-[#F9F7F4]/50 transition-colors">
                    <td className="py-3 px-4 text-[#1C211F] font-medium">{c.caregiver_name || "—"}</td>
                    <td className="py-3 px-4 text-[#1C211F]">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#2E5C50]" strokeWidth={1.5} />
                        {c.credential_type_label}
                        {c.is_required && <span className="text-[10px] uppercase tracking-wider text-[#C07A60] font-semibold">Required</span>}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[#57605A]">{c.issuing_authority || "—"}</td>
                    <td className="py-3 px-4 text-[#57605A]">{fmtDate(c.expiration_date)}</td>
                    <td className="py-3 px-4"><StatusBadge status={c.status} /></td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <button data-testid={`creds-download-${c.id}`} onClick={() => downloadFile(c)} className="inline-flex items-center gap-1 text-[#2E5C50] hover:text-[#22463D] text-sm font-medium mr-3">
                        <Download className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button data-testid={`creds-delete-${c.id}`} onClick={() => remove(c)} className="inline-flex items-center gap-1 text-[#B54D40] hover:opacity-80 text-sm font-medium">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CredentialUploadDialog
        open={showUpload}
        onClose={() => setShowUpload(false)}
        employees={employees}
        onUploaded={refresh}
      />
    </Layout>
  );
}
